import type React from 'react'
import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChatPanel } from '@/components/chat-panel'
import { PreviewPanel } from '@/components/preview-panel'
import { ResponsesTab } from '@/components/responses-tab'
import { PlanSummaryTab } from '@/components/plan-summary-tab'
import { PublishButton } from '@/components/publish-modal'
import { useRealtimePlan } from '@/hooks/use-realtime-plan'
import { useProfile } from '@/hooks/use-profile'
import { createPlan } from '@/lib/plan-api-client'

type Tab = 'design' | 'responses' | 'summary'

export function PlanDetailPage(): React.ReactElement {
  const { planId, orgSlug } = useParams<{ planId: string; orgSlug: string }>()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('design')

  const isNew = planId === 'new'
  const resolvedPlanId = isNew ? null : (planId ?? null)

  const { plan, loading } = useRealtimePlan(resolvedPlanId)
  const { organizations } = useProfile()

  const organizationId = useMemo(() => {
    if (plan?.organizationId) return plan.organizationId
    const org = organizations.find((o) => o.slug === orgSlug)
    return org?.id ?? null
  }, [plan?.organizationId, organizations, orgSlug])

  useEffect(() => {
    if (!isNew || creating) return
    setCreating(true)

    createPlan(orgSlug ?? '', 'Untitled Plan')
      .then((created) => {
        navigate(`/${orgSlug}/plans/${created.id}`, { replace: true })
      })
      .catch(() => {
        setCreating(false)
      })
  }, [isNew, creating, orgSlug, navigate])

  if (isNew) {
    return (
      <main className="flex h-full items-center justify-center" data-testid="creating-plan">
        <p className="text-sm text-neutral-500">Creating plan...</p>
      </main>
    )
  }

  return (
    <main className="flex h-full">
      <section className="w-1/3 shrink-0 border-r border-neutral-200" data-testid="chat-pane">
        <ChatPanel planId={resolvedPlanId} organizationId={organizationId} />
      </section>
      <section className="flex flex-1 flex-col" data-testid="preview-pane">
        <div className="flex items-center justify-between border-b border-neutral-200 pr-4">
          <div className="flex">
            <button
              type="button"
              onClick={() => setActiveTab('design')}
              className={[
                'px-4 py-2 text-sm font-medium transition-colors',
                activeTab === 'design'
                  ? 'border-b-2 border-brand-500 text-brand-700'
                  : 'text-neutral-500 hover:text-neutral-700',
              ].join(' ')}
              data-testid="tab-design"
            >
              Design
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('responses')}
              className={[
                'px-4 py-2 text-sm font-medium transition-colors',
                activeTab === 'responses'
                  ? 'border-b-2 border-brand-500 text-brand-700'
                  : 'text-neutral-500 hover:text-neutral-700',
              ].join(' ')}
              data-testid="tab-responses"
            >
              Responses
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('summary')}
              className={[
                'px-4 py-2 text-sm font-medium transition-colors',
                activeTab === 'summary'
                  ? 'border-b-2 border-brand-500 text-brand-700'
                  : 'text-neutral-500 hover:text-neutral-700',
              ].join(' ')}
              data-testid="tab-summary"
            >
              Summary
            </button>
          </div>
          {plan && <PublishButton plan={plan} onPublished={() => {}} />}
        </div>
        <div className="flex-1 overflow-hidden">
          {activeTab === 'design' && (
            <PreviewPanel plan={plan} loading={loading} />
          )}
          {activeTab === 'responses' && plan && (
            <ResponsesTab plan={plan} />
          )}
          {activeTab === 'responses' && !plan && (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-neutral-500">Loading plan...</p>
            </div>
          )}
          {activeTab === 'summary' && plan && (
            <PlanSummaryTab plan={plan} />
          )}
          {activeTab === 'summary' && !plan && (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-neutral-500">Loading plan...</p>
            </div>
          )}
        </div>
      </section>
    </main>
  )
}
